import type { AxiosResponse } from "axios";

import { getData, postData } from "@/apis/apiInstance";

import type {
  AddDisbursementPayloadType,
  ApprovalPayloadType,
  EditDisbursementPayloadType,
  GetDisbursementPayloadType,
  SingleDisbursementPayloadType,
} from "@/commonTypes/disbursementsTypes";

import { getCSRFToken } from "@/utils/commonFunctions";

export async function getDisbursementsListApi(
  data: GetDisbursementPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    search_value,
    limit,
    offset,
    approved,
    attachment,
    paymentStatus,
  } = data;
  return await getData(`job/disbursement/list`, {
    params: {
      limit,
      offset,
      ...(search_value && { search_term: search_value }),
      ...(approved && { approved }),
      ...(attachment && { attachment }),
      ...(paymentStatus && { payment_status: paymentStatus }),
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

export async function getSharedDisbursementsListApi(
  data: GetDisbursementPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    search_value,
    limit,
    offset,
    approved,
    attachment,
    paymentStatus,
  } = data;
  return await getData(`job/disbursement/shared/list`, {
    params: {
      limit,
      offset,
      search_term: search_value,
      ...(approved && { approved }),
      ...(attachment && { attachment }),
      ...(paymentStatus && { payment_status: paymentStatus }),
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}

export async function getSingleDisbursementApi(
  data: SingleDisbursementPayloadType
): Promise<AxiosResponse> {
  const { token, disbursement_id } = data;
  return await getData(`job/disbursement/id`, {
    params: {
      disbursement_id,
    },
    headers: {
      "user-token": token,
      "Content-Type": "application/json",
    },
  });
}
export async function addDisbursementsApi(
  data: AddDisbursementPayloadType
): Promise<AxiosResponse> {
  const { token, paymentDetails } = data;
  function findJobTypeAndId(job: string) {
    const index = job.lastIndexOf("(");
    let job_type = "";
    for (let i = 0; i < index; i++) {
      job_type += job[i];
    }
    return job_type;
  }
  paymentDetails?.forEach((e: any) => {
    e.amount_received = e?.amountReceive ? e?.amountReceive : null;
    e.mode_of_payment = e?.modeOfPayment;
    e.date_of_payment = e?.dateOfPayment ? e?.dateOfPayment : null;
    e.narration = e?.narration;
    delete e.amountReceive;
    delete e.modeOfPayment;
    delete e.dateOfPayment;
  });

  const finalData = {
    client_code: data?.clientId,
    client_job_id: data?.jobSelected?.value,
    job_type:
      data?.jobSelected?.label !== "Select Job..."
        ? findJobTypeAndId(data?.jobSelected?.label)
        : "Select Job...",
    title: data?.title?.trim(),
    amount: data?.amount ? data?.amount : null,
    payment_date: data?.date,
    payment_description: data?.descriptionOfPayment?.trim(),
    mode_of_payment: data?.modeOfPayment?.trim(),
    attachment: data?.uploadData,
    client_received_details: paymentDetails,
    access_member_ids: data?.accessMemberIds,
  };
  return await postData(`job/disbursement/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
export async function editDisbursementsApi(
  data: EditDisbursementPayloadType
): Promise<AxiosResponse> {
  const { token, paymentDetails } = data;

  function findJobTypeAndId(job: string) {
    const index = job.lastIndexOf("(");
    let job_type = "";
    for (let i = 0; i < index; i++) {
      job_type += job[i];
    }
    return job_type;
  }

  paymentDetails?.forEach((e: any) => {
    e.amount_received = e?.amountReceive ? e?.amountReceive : null;
    e.mode_of_payment = e?.modeOfPayment;
    e.date_of_payment = e?.dateOfPayment ? e?.dateOfPayment : null;
    e.narration = e?.narration;
    delete e.amountReceive;
    delete e.modeOfPayment;
    delete e.dateOfPayment;
  });
  const finalData = {
    client_code: data?.clientId,
    client_job_id: data?.jobSelected?.value,
    job_type:
      data?.jobSelected?.label !== "Select Job..."
        ? findJobTypeAndId(data?.jobSelected?.label)
        : "Select Job...",
    title: data?.title?.trim(),
    amount: data?.amount ? data?.amount : null,
    payment_date: data?.date,
    payment_description: data?.descriptionOfPayment?.trim(),
    mode_of_payment: data?.modeOfPayment?.trim(),
    attachment: data?.uploadData,
    disbursement_id: data?.disbursementId,
    client_received_details: paymentDetails,
    access_member_ids: data?.accessMemberIds,
    shared_with: data?.shared_with,
  };
  return await postData(`job/disbursement/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function disbursementApproval(
  data: ApprovalPayloadType
): Promise<AxiosResponse> {
  const { token, disbursementId } = data;
  let finalData = {
    disbursement_id: disbursementId,
  };

  return await postData(`job/disbursement/approve`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
